import { useMemo, useState } from "react"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"

import { PlaceholderData } from "@/components/lexical/placeholders"

interface PlaceholderPickerPopoverProps {
  placeholders: PlaceholderData[]
  onSelect: (placeholder: PlaceholderData) => void
  onCloseAutoFocus?: React.ComponentProps<typeof PopoverContent>["onCloseAutoFocus"]
  children: React.ReactNode
}

export function PlaceholderPickerPopover({
  placeholders,
  onSelect,
  onCloseAutoFocus,
  children,
}: PlaceholderPickerPopoverProps) {
  const [open, setOpen] = useState(false)

  const groupedPlaceholders = useMemo(() => {
    return placeholders.reduce(
      (acc, placeholder) => {
        if (!acc[placeholder.category]) {
          acc[placeholder.category] = []
        }
        acc[placeholder.category].push(placeholder)
        return acc
      },
      {} as Record<string, PlaceholderData[]>
    )
  }, [placeholders])

  const handleSelect = (placeholder: PlaceholderData) => {
    onSelect(placeholder)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0"
        align="start"
        side="top"
        sideOffset={4}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <Command
          filter={(value, search) => {
            const placeholder = placeholders.find((p) => p.id === value)
            if (!placeholder) return 0
            const searchLower = search.toLowerCase()
            return placeholder.label.toLowerCase().includes(searchLower) ||
              (placeholder.displayValue ?? placeholder.value).toLowerCase().includes(searchLower)
              ? 1
              : 0
          }}
        >
          <CommandInput placeholder="Search variables..." />
          <CommandList>
            <CommandEmpty>No variables found</CommandEmpty>
            {Object.entries(groupedPlaceholders).map(([category, items]) => (
              <CommandGroup key={category} heading={category}>
                {items.map((placeholder) => (
                  <CommandItem
                    key={placeholder.id}
                    value={placeholder.id}
                    onSelect={() => handleSelect(placeholder)}
                    className="flex w-[280px] justify-between gap-3 truncate"
                  >
                    <span className="block w-fit flex-none">{placeholder.label}</span>
                    <span className="text-muted-foreground block max-w-[150px] shrink truncate text-[10px]">
                      {placeholder.displayValue ?? placeholder.value}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
